package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.components.PlainLinkStyle
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LExternalLinkIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LSquarePenIcon
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTableSelectAllHeaderCell
import dev.lingvo.site.ds.LTableSelection
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * How many dots a coverage cell draws before it starts counting instead.
 *
 * A project with thirty target languages drew thirty 10px dots in one table cell — a 340px column
 * of decoration that pushed the source string, the thing the row is actually about, off the side of
 * a laptop. Eight is where the strip is still countable at a glance.
 */
internal const val COVERAGE_DOT_CAP = 8

@Composable
internal fun KeysTable(
    rows: List<KeyRow>, targetLocales: List<String>, coverage: Map<String, Map<String, TranslationState>>,
    filesById: Map<String, FileDto>, total: Int, selected: Set<String>,
    sourceLocale: String, slug: String, linkLocale: String?,
    canEditStructure: Boolean,
    selectAllHeaderId: String = "project-keys-select-all",
    onSelected: (String, Boolean) -> Unit, onSelectAll: (Boolean) -> Unit,
    onEdit: (KeyRow) -> Unit, onDelete: (KeyRow) -> Unit, busy: Boolean,
    /**
     * Opens one string's permalink, or null where there is nowhere to go.
     *
     * A callback rather than a `Link`, for the same reason every other navigation out of this
     * composable is one: the authority tests render it directly, outside any `@Page`, where
     * `PageContext` does not exist.
     */
    onOpenPermalink: ((KeyRow) -> Unit)? = null,
) {
    val selection = when {
        rows.isEmpty() || rows.none { it.key.id in selected } -> LTableSelection.NONE
        rows.all { it.key.id in selected } -> LTableSelection.ALL
        else -> LTableSelection.SOME
    }
    // The caption stays as the table's accessible name and the region's label; it stops being
    // painted because `KeysFilterBar` already states the same count, in different words, 40px above
    // — and the summary under the controls that decided it is the one the spec asks for.
    LTable(caption = resultCaption(total, rows.size), captionHidden = true) {
        LTableHead { LTableRow {
            if (canEditStructure) {
                LTableSelectAllHeaderCell(
                    state = selection,
                    onChange = onSelectAll,
                    label = t("app.projects.slug.keys_table.select_every_key_on_this_page", "Select every key on this page"),
                    modifier = Modifier.attr("id", selectAllHeaderId),
                )
            }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.name", "Name")) }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.source_string", "Source string")) }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.file", "File")) }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.tags", "Tags")) }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.type", "Type")) }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.coverage", "Coverage")) }
            LTableHeaderCell { Text(t("app.projects.slug.keys_table.actions", "Actions")) }
        } }
        LTableBody { rows.forEach { row ->
            LTableRow(
                selected = row.key.id in selected,
                selectable = canEditStructure,
                onSelectedChange = { checked -> onSelected(row.key.id, checked) },
                // Names the row, not the column: one row's checkbox is otherwise indistinguishable
                // from the next in a screen reader's forms list.
                selectionLabel = tf(
                    "app.projects.slug.keys_table.select_named_key",
                    "Select {name}",
                    "name" to row.key.name,
                ),
            ) {
                LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                    // The name is the row's subject and the way into the work: before this the list
                    // could find a key and not open it, the only row actions being a metadata modal
                    // and delete.
                    linkLocale?.let { locale ->
                        Link(
                            path = "/app/projects/$slug/translate/$locale?key=${row.key.id}",
                            modifier = PlainLinkStyle.toModifier().monoFont().fontSize(TypeScale.BodySmall),
                        ) { Text(row.key.name) }
                    } ?: Span(Modifier.monoFont().fontSize(TypeScale.BodySmall).toAttrs()) { Text(row.key.name) }
                }
                LTableCell {
                    Span(
                        Modifier.styleModifier { property("max-width", "320px") }.toAttrs {
                            // Han is unified in Unicode: without `lang` a reviewer whose locale
                            // isn't the source's reads someone else's glyph forms.
                            attr("lang", sourceLocale)
                            attr("dir", "auto")
                        },
                    ) { Text(row.sourceText()) }
                }
                LTableCell {
                    // **Capped like the source cell beside it.** The full mono path repeats on
                    // every row at ~310px, which pushed the table's intrinsic width past the
                    // shell's 1128px column and carried the whole actions column — Edit, permalink,
                    // Delete — outside the container, where an overlay scrollbar draws nothing to
                    // say so. The `title` keeps the full path one hover away.
                    val path = filesById[row.key.fileId]?.path
                        ?: tr("app.projects.slug.keys_table.unknown_file", "Unknown file")
                    Span(
                        Modifier
                            .monoFont()
                            .styleModifier {
                                property("display", "inline-block")
                                property("max-width", "220px")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                                property("vertical-align", "middle")
                            }
                            .toAttrs {
                                attr("title", path)
                                attr("dir", "ltr")
                            },
                    ) {
                        Text(path)
                    }
                }
                LTableCell { Row(Modifier.gap(Space.XS).flexWrap(FlexWrap.Wrap)) {
                    if (row.key.tags.isEmpty()) Text("—") else row.key.tags.forEach { LChip(it) }
                } }
                LTableCell { LBadge(
                    if (row.key.isPlural) {
                        t("app.projects.slug.keys_table.plural", "Plural")
                    } else {
                        t("app.projects.slug.keys_table.single", "Single")
                    },
                    // Neutral for both arms. Plural is a key's *shape*, not a state — neither
                    // better nor worse nor actionable — and the accent on this page belongs to
                    // "Add key" and the nav's current destination. The word carries the difference.
                    variant = LBadgeVariant.NEUTRAL,
                ) }
                LTableCell { CoverageCell(targetLocales, coverage[row.key.id].orEmpty()) }
                LTableCell { Row(Modifier.gap(Space.XS)) {
                    // **Glyphs, and every one of them names the key it acts on.** Two named
                    // buttons per row put "Edit Delete" beside four hundred rows; the conventional
                    // pencil and bin carry the meaning, and the accessible name — which is what a
                    // screen reader's control list actually reads — keeps the row's subject.
                    if (canEditStructure) {
                        LIconAction(
                            label = tf("app.projects.slug.keys_table.edit_named_key", "Edit {name}", "name" to row.key.name),
                            onClick = { onEdit(row) },
                            size = LButtonSize.SM,
                            enabled = !busy,
                        ) { LSquarePenIcon() }
                    }
                    // Offered to every role: the permalink is how somebody sends this string to a
                    // colleague, and reading a project is not a smaller need than editing one.
                    onOpenPermalink?.let { open ->
                        LIconAction(
                            label = tf(
                                "app.projects.slug.keys_table.open_named_key_in_every_language",
                                "Open {name} in every language",
                                "name" to row.key.name,
                            ),
                            onClick = { open(row) },
                            size = LButtonSize.SM,
                        ) { LExternalLinkIcon() }
                    }
                    if (canEditStructure) {
                        LIconAction(
                            label = tf("app.projects.slug.keys_table.delete_named_key", "Delete {name}", "name" to row.key.name),
                            onClick = { onDelete(row) },
                            // Ghost, like the two beside it and like every other per-row remove in
                            // the product (a provider credential, for one). The filled destructive
                            // ground is for the deliberate, page-level act — "Delete selected" in
                            // the bulk bar, the danger zone — and painted once per row it put 45
                            // solid red buttons down the loudest column of the page. The trash
                            // glyph and the confirmation carry the intent.
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                            enabled = !busy,
                        ) { LTrashIcon() }
                    }
                } }
            }
        } }
    }
}

/**
 * One coverage cell in place of the `N`-locale columns it replaces.
 *
 * A strip of dots carries exactly what the old columns did, at a tenth of the width — but a dot has
 * no name, so a screen reader used to hear eight unlabelled `role="img"` elements per row. This wraps
 * the whole strip in one `role="img"` with one label naming what actually needs naming: which
 * locales, if any, are missing. Countable, so it leads with the count rather than only the list.
 *
 * **The strip is capped and the remainder is a number.** The accessible label is not capped: it
 * still names every missing language, because the reader who cannot see the dots is the one who
 * most needs the list.
 */
@Composable
private fun CoverageCell(targetLocales: List<String>, states: Map<String, TranslationState>) {
    if (targetLocales.isEmpty()) {
        Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) { Text(t("app.projects.slug.keys_table.no_target_locales", "No target locales")) }
        return
    }
    val palette = ColorMode.current.toSitePalette()
    val missing = targetLocales.filter { (states[it] ?: TranslationState.EMPTY) == TranslationState.EMPTY }
    val translated = targetLocales.size - missing.size
    val summary = if (missing.isEmpty()) {
        tf(
            "app.projects.slug.keys_table.coverage_complete",
            "{translated} of {locales}",
            "translated" to translated,
            "locales" to tPlural(
                "app.projects.slug.keys_table.count.locale_translated",
                targetLocales.size,
                "{count} locale translated",
                "{count} locales translated",
            ),
        )
    } else {
        tf(
            "app.projects.slug.keys_table.coverage_with_gaps",
            "{translated} of {locales}; missing in {missing}",
            "translated" to translated,
            "locales" to tPlural(
                "app.projects.slug.keys_table.count.locale_translated",
                targetLocales.size,
                "{count} locale translated",
                "{count} locales translated",
            ),
            "missing" to missing.joinToString(", ") { localeInfo(it).englishName },
        )
    }
    val drawn = targetLocales.take(COVERAGE_DOT_CAP)
    val hidden = targetLocales.size - drawn.size
    Row(
        Modifier.gap(Space.XXS).attr("role", "img").attr("aria-label", summary).attr("title", summary),
    ) {
        drawn.forEach { code ->
            val state = states[code] ?: TranslationState.EMPTY
            // Same progression as the editor's gutter — nothing, grey, green — so the two surfaces
            // teach the same thing.
            val color = when (state) {
                TranslationState.EMPTY -> Colors.Transparent
                TranslationState.TRANSLATED -> palette.textMuted
                TranslationState.APPROVED -> palette.success
            }
            Span(
                Modifier.width(10.px).height(10.px).backgroundColor(color).borderRadius(Radius.Pill)
                    .border(1.px, LineStyle.Solid, if (state == TranslationState.EMPTY) palette.borderStrong else color)
                    // The group above carries the label; an aria-label on every dot is exactly the
                    // per-cell noise this replaces.
                    .attr("aria-hidden", "true")
                    .toAttrs(),
            )
        }
        if (hidden > 0) {
            Span(
                Modifier.fontSize(TypeScale.Small).toAttrs { attr("aria-hidden", "true") },
            ) { Text("+$hidden") }  // i18n-ignore — a plus sign and a digit; the label above says it in words
        }
    }
}

private fun resultCaption(total: Int, shown: Int): String =
    if (shown >= total) {
        tPlural("app.projects.slug.keys_table.count.result", total, "{count} key result", "{count} key results")
    } else {
        trf(
            "app.projects.slug.keys_table.caption_shown_of_total",
            "{shown} of {total}",
            "shown" to shown,
            "total" to tPlural(
                "app.projects.slug.keys_table.count.result",
                total,
                "{count} key result",
                "{count} key results",
            ),
        )
    }

private fun KeyRow.sourceText(): String = source.value
    ?: source.pluralForms.orEmpty().entries.sortedBy { it.key.ordinal }.joinToString(" · ") {
        "${it.key.name.lowercase()}: ${it.value}"
    }.ifBlank { tr("app.projects.slug.keys_table.no_source_string", "No source string") }
